import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Users, ArrowRight, AlertTriangle, Clock, RefreshCw, Loader2,
  TrendingUp, XCircle, CheckCircle2, BarChart3,
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_LABELS = {
  "funnel.business_selected": { label: "Business Selected", icon: Users, color: "blue" },
  "funnel.lead_submitted": { label: "Email Submitted", icon: ArrowRight, color: "indigo" },
  "funnel.keywords_loaded": { label: "Keywords Loaded", icon: TrendingUp, color: "purple" },
  "funnel.keywords_selected": { label: "Audit Started", icon: CheckCircle2, color: "emerald" },
  "funnel.audit_completed": { label: "Audit Completed", icon: CheckCircle2, color: "green" },
  "funnel.audit_failed": { label: "Audit Failed", icon: XCircle, color: "red" },
  "funnel.report_viewed": { label: "Report Viewed", icon: BarChart3, color: "teal" },
};

const FUNNEL_ORDER = [
  "funnel.business_selected",
  "funnel.lead_submitted",
  "funnel.keywords_loaded",
  "funnel.keywords_selected",
  "funnel.audit_completed",
  "funnel.report_viewed",
];

export default function FunnelTab({ adminToken }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/funnel/stats`, { headers: { Authorization: `Bearer ${adminToken}` } });
      setData(res.data);
    } catch { setData(null); }
    setLoading(false);
  }, [adminToken]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (!data) return <p className="text-center text-slate-400 py-16">Failed to load funnel data.</p>;

  const counts = data.event_counts || {};
  const timings = data.step_timings || {};
  const errors = data.recent_errors || [];

  // Calculate drop-off rates
  const dropoffs = [];
  for (let i = 1; i < FUNNEL_ORDER.length; i++) {
    const prev = counts[FUNNEL_ORDER[i - 1]] || 0;
    const curr = counts[FUNNEL_ORDER[i]] || 0;
    const rate = prev > 0 ? Math.round(((prev - curr) / prev) * 100) : 0;
    dropoffs.push({ from: FUNNEL_ORDER[i - 1], to: FUNNEL_ORDER[i], rate, lost: prev - curr });
  }

  return (
    <div className="space-y-6" data-testid="funnel-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-[#111827]">Audit Funnel Analytics</h2>
          <p className="text-sm text-[#6B7280]">Last 7 days — track where users drop off</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData}><RefreshCw className="w-4 h-4" /></Button>
      </div>

      {/* Funnel Visualization */}
      <Card className="bg-white border-[#E5E7EB]">
        <CardHeader><CardTitle className="text-base text-[#111827]">Conversion Funnel</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-2">
            {FUNNEL_ORDER.map((event, i) => {
              const info = EVENT_LABELS[event] || { label: event, color: "slate" };
              const count = counts[event] || 0;
              const maxCount = Math.max(...FUNNEL_ORDER.map(e => counts[e] || 0), 1);
              const widthPct = Math.max(5, (count / maxCount) * 100);
              const dropoff = i > 0 ? dropoffs[i - 1] : null;

              return (
                <div key={event}>
                  {dropoff && dropoff.rate > 0 && (
                    <div className="flex items-center gap-2 py-1 pl-2">
                      <AlertTriangle className="w-3 h-3 text-amber-500" />
                      <span className="text-xs text-amber-600 font-medium">
                        {dropoff.rate}% drop-off ({dropoff.lost} users lost)
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <div className="w-36 text-right">
                      <p className="text-xs font-medium text-[#6B7280]">{info.label}</p>
                    </div>
                    <div className="flex-1 h-8 bg-slate-50 rounded overflow-hidden relative">
                      <div
                        className="h-full rounded transition-all duration-500"
                        style={{
                          width: `${widthPct}%`,
                          background: `var(--color-${info.color || "blue"}, #6366f1)`,
                          backgroundColor: info.color === "red" ? "#ef4444" : info.color === "green" ? "#22c55e" : info.color === "emerald" ? "#10b981" : info.color === "purple" ? "#8b5cf6" : info.color === "indigo" ? "#6366f1" : info.color === "teal" ? "#14b8a6" : "#3b82f6",
                          opacity: 0.85,
                        }}
                      />
                      <span className="absolute inset-0 flex items-center justify-center text-xs font-bold" style={{ color: count > 0 ? "#1e293b" : "#94a3b8" }}>
                        {count}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Failed audits */}
          {(counts["funnel.audit_failed"] || 0) > 0 && (
            <div className="mt-3 flex items-center gap-2 p-2 rounded bg-red-50 border border-red-100">
              <XCircle className="w-4 h-4 text-red-500" />
              <span className="text-xs font-medium text-red-700">{counts["funnel.audit_failed"]} failed audit attempts</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Step Timings */}
      {Object.keys(timings).length > 0 && (
        <Card className="bg-white border-[#E5E7EB]">
          <CardHeader><CardTitle className="text-base text-[#111827] flex items-center gap-2"><Clock className="w-4 h-4 text-indigo-500" /> Step Timings</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(timings).map(([event, stats]) => (
                <div key={event} className="flex items-center justify-between p-2 rounded bg-slate-50">
                  <span className="text-sm text-[#374151]">{EVENT_LABELS[event]?.label || event}</span>
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className="text-xs">avg {(stats.avg_ms / 1000).toFixed(1)}s</Badge>
                    <Badge variant="outline" className="text-xs text-amber-600">max {(stats.max_ms / 1000).toFixed(1)}s</Badge>
                    <span className="text-xs text-[#9CA3AF]">{stats.count} samples</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Errors */}
      {errors.length > 0 && (
        <Card className="bg-white border-[#E5E7EB]">
          <CardHeader><CardTitle className="text-base text-[#111827] flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-500" /> Recent Failures</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {errors.map((err, i) => (
                <div key={i} className="p-3 rounded border border-red-100 bg-red-50/50 text-sm">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-[#111827]">{err.email || "anonymous"}</span>
                    <span className="text-xs text-[#9CA3AF]">{err.created_at ? new Date(err.created_at).toLocaleString() : ""}</span>
                  </div>
                  <p className="text-xs text-red-600">{err.error || "Unknown error"}</p>
                  {err.place_id && <p className="text-xs text-[#9CA3AF] mt-0.5">Place: {err.place_id}</p>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
